<script lang="ts" setup>
import { SortDirection } from '~/types/searchTypes'
import type { CategoryItem, Product, RawProduct } from '~/types/searchTypes'
import ProductCarousel from '../ProductCarousel.vue'
import { useCategoryStore } from '~/store/categoryStore'
import { useLanguageStore } from '~/store/language'
import computeAllCategoryChildrenIdsByParentIds from '~/utils/config/search/computeAllCategoryChildrenIdsByParentIds'
import getUniqueProducts from '~/utils/search/getUniqueProducts'
import ProductLimitEnum from '~/enums/widgets/ProductLimitEnum'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const products = ref<Product[]>([])
const categoryStore = useCategoryStore()
const locale = useState<string>('locale.setting')

const props = defineProps<{
  category?: number
  sort?: keyof typeof SortDirection
  title?: string
}>()

const categoryId = ref(props.category)

watch(
  () => props,
  (properties) => {
    categoryId.value = properties.category
  },
  {
    immediate: true,
    deep: true,
  }
)

watch(
  (): [CategoryItem[], number | undefined] => [categoryStore.getCategories, categoryId.value],
  async ([categoryItems, category]) => {
    try {
      let categories: undefined | number[]

      if (category) {
        categories = computeAllCategoryChildrenIdsByParentIds([category], categoryItems)
      }

      const response = await $fetch<RawProduct[]>('/api/hooks/widgetProducts', {
        method: 'POST',
        body: {
          locale: locale.value,
          limit: 30,
          categories,
          sort: props.sort,
          languageId: useLanguageStore().getLanguageId,
        },
        ...fetchRetryParameters,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      const fResponse = getUniqueProducts(response)

      if (fResponse.length < ProductLimitEnum.Threshold && category) {
        const rootCategory = categoryStore.getCategoryTree.find((rootCat) => rootCat?.children_ids?.includes(category))

        if (category !== rootCategory?.id) {
          categoryId.value = rootCategory?.id
        }
      } else {
        products.value = fResponse.sort(() => 0.5 - Math.random()).splice(0, 10)
      }
    } catch (error) {}
  },
  {
    immediate: true,
    deep: true,
  }
)
</script>

<template>
  <section v-if="products?.length">
    <Container>
      <h4 class="w-full" v-text="props.title" />
    </Container>
    <ProductCarousel :products="products" />
  </section>
</template>
